import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, X, MapPin } from "lucide-react";

const LINKS = [
    { label: "About", href: "#about" },
    { label: "Menu", href: "#menu" },
    { label: "Treats", href: "#treats" },
    { label: "Gallery", href: "#gallery" },
    { label: "Reviews", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
                scrolled
                    ? "bg-[#FFFDF9]/90 backdrop-blur-xl border-[#F2E1E6] shadow-[0_8px_30px_rgba(61,35,24,0.06)]"
                    : "bg-transparent border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <a href="#home" data-testid="nav-logo-link" className="flex items-center gap-3">
                        <img
                            src="/annabells-logo.png"
                            alt="Annabells Cupcakes and More logo"
                            className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-md"
                        />
                        <span className="leading-none">
                            <span className="font-script text-2xl text-[#C95B7D] block">Annabells</span>
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8C6A5D] font-semibold">
                                Cupcakes &amp; More
                            </span>
                        </span>
                    </a>

                    <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="text-sm font-medium text-[#6B4A3E] hover:text-[#C95B7D] transition-colors duration-300"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-4">
                        <span className="hidden xl:flex items-center gap-1.5 text-xs text-[#8C6A5D]" data-testid="nav-service-area">
                            <MapPin className="h-3.5 w-3.5 text-[#C59B27]" />
                            Shelton · Olympia · Lacey
                        </span>
                        <a
                            href="#order"
                            data-testid="nav-order-button"
                            className="rounded-full bg-[#C95B7D] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(201,91,125,0.35)] hover:bg-[#b44a6b] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
                        >
                            Request an Order
                        </a>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden p-2 text-[#3D2318]"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-[#FFFDF9]/95 backdrop-blur-xl border-t border-[#F2E1E6]"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-xl text-[#3D2318] hover:text-[#C95B7D] transition-colors"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#order"
                                data-testid="nav-mobile-order-button"
                                onClick={() => setOpen(false)}
                                className="mt-2 rounded-full bg-[#C95B7D] px-5 py-3 text-center text-sm font-semibold text-white"
                            >
                                Request an Order
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
